import PropTypes from 'prop-types';

import { RWebShare } from 'react-web-share';

import Stack, { StackProps as StackProps } from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { Icons } from '@classity-edtech/ui-base';
import { Box, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { ArrowRightIcon } from '@classity-edtech/ui-base/dist/components/Displays';
import { useNavigate } from 'react-router-dom';
import { webPaths } from '../../../routes/paths';
import { FormattedMessage } from 'react-intl';

export interface SideNavProps extends StackProps {
  showSideNav: boolean;
  toggleSideNav: () => void;
  shareData: {
    text: string;
    url: string;
    title: string;
  };
}

export const SideNav = ({
  className,
  showSideNav,
  toggleSideNav,
  shareData,
  ...props
}: SideNavProps) => {
  const navigate = useNavigate();

  const navigateToDashboardHome = () => {
    toggleSideNav();
    navigate(`/${webPaths.dashboard}`);
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <>
      <Stack sx={{ gap: 1, width: '140px', display: { xs: 'none', md: 'flex' } }} {...props}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            cursor: 'pointer',
            '&:hover': {
              color: globalColours['$global-colour-interactive-primary-hover'],
            },
          }}
          onClick={navigateToDashboardHome}
        >
          <Icons.Network sx={{ width: '16px', height: '16px' }} />
          <Typography
            variant='body3SemiBold'
            sx={{ color: globalColours['$global-colour-interactive-primary-active'] }}
          >
            {localisedMessage('home')}
          </Typography>
        </Box>

        <RWebShare data={shareData}>
          <Box
            data-event='call_to_action_click'
            data-traffic-type={process.env.NODE_ENV === 'production' ? 'production' : 'local'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              cursor: 'pointer',
              '&:hover': {
                color: globalColours['$global-colour-interactive-primary-hover'],
              },
            }}
          >
            <Icons.Export sx={{ width: '16px', height: '16px' }} />
            <Typography
              variant='body3'
              sx={{ color: globalColours['$global-colour-interactive-primary-default'] }}
            >
              {localisedMessage('refer.friend')}
            </Typography>
          </Box>
        </RWebShare>
      </Stack>

      <Stack
        sx={{
          transition: 'all 0.5s',
          gap: 1,
          height: '100%',
          width: { xs: '100%', md: '140px' },
          position: { xs: 'fixed', md: 'static' },
          zIndex: 10,
          background: globalColours['$global-colour-background-elevated'],
          top: 0,
          left: showSideNav ? '0' : '-100%',
          display: { xs: 'flex', md: 'none' },
        }}
        {...props}
      >
        <Box sx={{ px: 2.5, py: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '1 / 1',
              width: '48px',
              background: globalColours['$global-colour-background-overlay'],
              borderRadius: '100%',
              cursor: 'pointer',
              ml: 'auto',
            }}
            onClick={toggleSideNav}
          >
            <Icons.Cancel
              sx={{
                width: '20px',
                height: '20px',
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1,
            cursor: 'pointer',
          }}
          onClick={navigateToDashboardHome}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              '&:hover': {
                color: globalColours['$global-colour-interactive-primary-hover'],
              },
            }}
          >
            <Icons.Network sx={{ width: '16px', height: '16px' }} />
            <Typography
              variant='body3SemiBold'
              sx={{ color: globalColours['$global-colour-interactive-primary-active'] }}
            >
              {localisedMessage('home')}
            </Typography>
          </Box>
          <ArrowRightIcon sx={{ width: '20px', height: '20px' }} />
        </Box>

        <RWebShare data={shareData}>
          <Box
            data-event='call_to_action_click'
            data-traffic-type={process.env.NODE_ENV === 'production' ? 'production' : 'local'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                color: globalColours['$global-colour-interactive-primary-hover'],
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Icons.Export sx={{ width: '16px', height: '16px' }} />
              <Typography
                variant='body3'
                sx={{ color: globalColours['$global-colour-interactive-primary-default'] }}
              >
                {localisedMessage('refer.friend')}
              </Typography>
            </Box>
            <ArrowRightIcon sx={{ width: '20px', height: '20px' }} />
          </Box>
        </RWebShare>
      </Stack>
    </>
  );
};

SideNav.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

SideNav.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(SideNav)(
  ({ theme }) => `
  &.MuiBox-root {
  }
`,
);
